/**
 * Brecha de activación: el ranking de empleados digitales contratados que no
 * están corriendo. Es el gráfico "dinero contratado que no está funcionando".
 *
 * Tres decisiones que valen la pena:
 *
 *  - El **total va arriba** y con su fórmula al lado, para que se pueda
 *    recalcular sumando las filas de abajo.
 *  - El **peor caso** tiene su propia tarjeta con el caso escrito en prosa. En
 *    una tabla de 25 filas ordenada por brecha, la primera fila se lee como "una
 *    fila más"; el objetivo de esta vista es que no se lea así.
 *  - Las cuentas **sin medición** se dicen en pantalla: la brecha real puede ser
 *    mayor que la que se ve, y esconder eso convierte un dato incompleto en un
 *    dato falso.
 *
 * El ranking sale de `/api/activation` (la autoridad del total) y se enriquece
 * con MRR, estado y antigüedad del portfolio ya cargado, uniendo por id.
 */
import {
  CLIENT_STATE_LABELS,
  SIN_DATO,
  formatMetricValue,
  formatPeriodo,
  type Client,
  type Links,
} from '@ops/contracts'

import { useActivation } from '../../lib/queries'
import { useVincular } from '../../lib/vincular'
import {
  Card,
  DataTable,
  ExternalLink,
  Panel,
  ProgressBar,
  Stack,
  StatusPill,
  type Column,
} from '../../ui'
import { KpiDerivado } from './KpiDerivado'
import { Cargando, ErrorDeCarga, Vacio } from '../comunes'
import {
  filasBrecha,
  peorCaso,
  saludActivacion,
  saludEstado,
  sumaMrr,
  type FilaBrecha,
} from './derivados'

export interface BrechaActivacionProps {
  periodo: string
  /** El portfolio ya cargado por la página: evita pedirlo dos veces. */
  clientes: readonly Client[]
  verPlata: boolean
  onAbrir: (clientId: string) => void
}

export function BrechaActivacion({
  periodo,
  clientes,
  verPlata,
  onAbrir,
}: BrechaActivacionProps) {
  const vincular = useVincular()
  const activacion = useActivation(periodo)

  if (activacion.isPending) return <Cargando que="la brecha de activación" />

  if (activacion.isError) {
    return (
      <ErrorDeCarga
        que="la brecha de activación"
        error={activacion.error}
        onRetry={() => void activacion.refetch()}
        reintentando={activacion.isFetching}
      />
    )
  }

  const { filas, totales, links } = activacion.data
  const conBrecha = filasBrecha(filas, clientes)
  const peor = peorCaso(conBrecha)
  const mrrEnBrecha = sumaMrr(conBrecha)

  return (
    <Stack gap="md">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(224px,1fr))] gap-3">
        <KpiDerivado
          titulo="Empleados contratados que no están corriendo"
          valor={formatMetricValue(totales.brecha, 'count')}
          derivacion="Σ (contratados − instalados)"
          base={`${filas.length} cuentas con medición en ${formatPeriodo(periodo)}`}
          severidad={totales.brecha > 0 ? 'critical' : 'ok'}
          nota={
            <>
              de {formatMetricValue(totales.contratados, 'count')} contratados ·{' '}
              {conBrecha.length} cuentas con brecha
            </>
          }
        />
        <KpiDerivado
          titulo="Activación global"
          valor={formatMetricValue(totales.pctActivado, 'percent')}
          derivacion="instalados / contratados"
          base={`${totales.instalados} de ${totales.contratados} empleados`}
          severidad={
            totales.pctActivado >= 85
              ? 'ok'
              : totales.pctActivado >= 40
                ? 'warning'
                : 'critical'
          }
          nota={`${formatMetricValue(totales.instalados, 'count')} corriendo · −${totales.brecha} contra lo contratado`}
        />
        {verPlata ? (
          <KpiDerivado
            titulo="MRR de las cuentas con brecha"
            valor={mrrEnBrecha === null ? null : formatMetricValue(mrrEnBrecha, 'usd')}
            derivacion="suma del MRR de las cuentas con brecha > 0"
            base={`${conBrecha.length} cuentas`}
            severidad="warning"
            detalleSinDato="ninguna cuenta con brecha tiene MRR cargado"
            nota="Es lo que se factura en cuentas que no terminaron de arrancar."
          />
        ) : null}
        <KpiDerivado
          titulo="Cuentas sin medición"
          valor={formatMetricValue(totales.sinMedicion, 'count')}
          derivacion="cuentas sin fila de activación en el período"
          base={`${clientes.length} cuentas del portfolio`}
          severidad={totales.sinMedicion > 0 ? 'warning' : 'ok'}
          nota={
            totales.sinMedicion > 0
              ? 'La brecha real puede ser mayor que la que se ve: estas cuentas no entran en el total.'
              : 'Todas las cuentas del portfolio reportan instalados.'
          }
        />
      </div>

      {peor !== null ? (
        <Card severidad="critical" padding="md" elevada>
          <Stack gap="xs" align="start">
            <StatusPill severidad="critical" caps size="sm">
              Peor caso del portfolio
            </StatusPill>
            <p className="text-meta text-text [&_b]:font-semibold">
              <b>{peor.clientNombre}</b> tiene{' '}
              <b>
                {peor.activacion.instalados} de {peor.activacion.contratados}
              </b>{' '}
              empleados corriendo — <b>−{peor.activacion.brecha} sin instalar</b>
              {peor.estado !== null ? (
                <>
                  {' '}
                  y lleva <b>{peor.diasEnEstado} días</b> en{' '}
                  {CLIENT_STATE_LABELS[peor.estado]}
                </>
              ) : null}
              {verPlata && peor.mrr !== null ? (
                <>
                  , facturando <b>{formatMetricValue(peor.mrr, 'usd')} por mes</b>
                </>
              ) : null}
              .
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => onAbrir(peor.clientId)}
                className="text-meta font-semibold text-accent-text underline decoration-from-font underline-offset-2 hover:text-text"
              >
                Abrir la ficha de {peor.clientNombre}
              </button>
              <ExternalLink
                link={links.clientes[peor.clientId]?.plataforma ?? null}
                variante="chip"
                sistemaFaltante="la plataforma"
                onVincular={() =>
                  vincular(`la ficha de ${peor.clientNombre} en la plataforma`)
                }
              >
                Ver por qué no arrancó
              </ExternalLink>
            </div>
          </Stack>
        </Card>
      ) : null}

      <Panel
        titulo="Ranking por empleados contratados sin instalar"
        subtitulo="De mayor brecha a menor. La fila crítica es la que no tiene ni uno corriendo."
        padding="none"
      >
        {conBrecha.length === 0 ? (
          <div className="p-3">
            <Vacio punteada
              mensaje="Ninguna cuenta con medición tiene brecha en este período."
              detalle={
                totales.sinMedicion > 0
                  ? `Ojo: ${totales.sinMedicion} cuentas no reportan instalados.`
                  : undefined
              }
            />
          </div>
        ) : (
          <TablaBrecha
            filas={conBrecha}
            linksClientes={links.clientes}
            verPlata={verPlata}
            periodo={periodo}
            onAbrir={onAbrir}
            totalBrecha={totales.brecha}
          />
        )}
      </Panel>
    </Stack>
  )
}

interface TablaBrechaProps {
  filas: readonly FilaBrecha[]
  linksClientes: Links['clientes']
  verPlata: boolean
  periodo: string
  onAbrir: (clientId: string) => void
  totalBrecha: number
}

function TablaBrecha({
  filas,
  linksClientes,
  verPlata,
  periodo,
  onAbrir,
  totalBrecha,
}: TablaBrechaProps) {
  const vincular = useVincular()

  // El puesto se calcula una vez sobre el orden por brecha: si se recalculara
  // con la posición de la fila, reordenar por MRR renumeraría el ranking y el
  // "puesto 1" dejaría de significar "la peor brecha".
  const puestos = new Map(filas.map((fila, indice) => [fila.clientId, indice + 1]))

  const columnas: Column<FilaBrecha>[] = [
    {
      key: 'rank',
      header: '#',
      align: 'right',
      width: '40px',
      sortValue: (fila) => puestos.get(fila.clientId) ?? null,
      render: (fila) => (
        <span className="text-muted-strong">{puestos.get(fila.clientId)}</span>
      ),
    },
    {
      key: 'cuenta',
      header: 'Cuenta',
      rowHeader: true,
      width: '208px',
      sortValue: (fila) => fila.clientNombre,
      filterValue: (fila) => `${fila.clientNombre} ${fila.vertical ?? ''}`,
      render: (fila) => (
        <span className="flex flex-col items-start gap-1">
          <span className="truncate">{fila.clientNombre}</span>
          <span className="text-micro font-normal text-muted-strong">
            {fila.vertical ?? 'vertical sin cargar'} ·{' '}
            {fila.owner?.nombre ?? 'sin dueño'}
          </span>
        </span>
      ),
    },
    {
      key: 'estado',
      header: 'Estado',
      width: '176px',
      sortValue: (fila) =>
        fila.estado === null ? null : CLIENT_STATE_LABELS[fila.estado],
      render: (fila) =>
        fila.estado === null ? (
          <span className="text-micro text-muted-strong">Sin estado</span>
        ) : (
          <StatusPill severidad={saludEstado(fila.estado)} size="sm">
            {CLIENT_STATE_LABELS[fila.estado]}
          </StatusPill>
        ),
    },
    {
      key: 'instalado',
      header: 'Instalado sobre contratado',
      width: '196px',
      sortValue: (fila) => fila.activacion.pctActivado,
      render: (fila) => (
        <span className="flex flex-col gap-1">
          <ProgressBar
            pct={fila.activacion.pctActivado}
            label={`Activación de ${fila.clientNombre}`}
            severidad={saludActivacion(fila.activacion)}
            size="sm"
            etiqueta={formatMetricValue(fila.activacion.pctActivado, 'percent')}
          />
          <span className="tabular text-micro text-muted-strong">
            <b className="font-semibold text-text">{fila.activacion.instalados}</b>{' '}
            corriendo de {fila.activacion.contratados}
          </span>
        </span>
      ),
    },
    {
      key: 'brecha',
      header: 'Sin instalar',
      align: 'right',
      width: '96px',
      sortValue: (fila) => fila.activacion.brecha,
      render: (fila) => (
        <span className="font-semibold text-danger-text">
          −{fila.activacion.brecha}
        </span>
      ),
    },
  ]

  if (verPlata) {
    columnas.push({
      key: 'mrr',
      header: 'MRR',
      align: 'right',
      width: '96px',
      sortValue: (fila) => fila.mrr,
      render: (fila) =>
        fila.mrr === null ? SIN_DATO : formatMetricValue(fila.mrr, 'usd'),
    })
  }

  columnas.push(
    {
      key: 'dias',
      header: 'Días en estado',
      align: 'right',
      width: '96px',
      sortValue: (fila) => fila.diasEnEstado,
      render: (fila) =>
        fila.diasEnEstado === null
          ? SIN_DATO
          : formatMetricValue(fila.diasEnEstado, 'count'),
    },
    {
      key: 'plataforma',
      header: 'En la plataforma',
      width: '160px',
      render: (fila) => (
        <ExternalLink
          link={linksClientes[fila.clientId]?.plataforma ?? null}
          variante="chip"
          sistemaFaltante="la plataforma"
          onVincular={() =>
            vincular(`la ficha de ${fila.clientNombre} en la plataforma`)
          }
        >
          Ver la cuenta
        </ExternalLink>
      ),
    },
  )

  return (
    <DataTable
      columns={columnas}
      rows={filas}
      rowKey={(fila) => fila.clientId}
      rowSeverity={(fila) => saludActivacion(fila.activacion)}
      densidadPorSeveridad
      onRowClick={(fila) => onAbrir(fila.clientId)}
      caption={`Cuentas con empleados digitales contratados sin instalar en ${formatPeriodo(periodo)}. Contratados e instalados salen de la plataforma; el estado y el MRR, del portfolio.`}
      footer={
        <span className="tabular">
          Total de la brecha: <b className="font-semibold text-text">{totalBrecha}</b>{' '}
          empleados sin instalar, sumando las {filas.length} filas de arriba.
        </span>
      }
    />
  )
}
